import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Button } from '@/components/ui';

/**
 * CheckoutCancel Page
 * 
 * Displayed when a user cancels the Stripe Checkout session
 * Provides options to go back to shopping or home
 */

const CheckoutCancel: React.FC = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    // Try to go back to previous page
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900 px-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="text-center">
            {/* Cancel Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              <svg
                className="h-10 w-10 text-gray-600 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Checkout Cancelled
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Your payment was not processed
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                What happened?
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                You cancelled the checkout process before completing your purchase.
                No charges have been made to your payment method.
              </p>
            </div>

            {/* Common Reasons */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Changed your mind?
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                That's okay! Here are some things you can do:
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <span>Go back to the shop and browse other products</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <span>Contact the academy if you have questions about the lessons</span>
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <span>Try again when you're ready - your cart items are still available</span>
                </li>
              </ul>
            </div>

            {/* Support Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Need Help?
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                If you encountered an error or have questions about the checkout process,
                please contact our support team or reach out to the academy directly.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={handleGoBack}
                variant="default"
                className="flex-1"
              >
                Go Back to Shop
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
              >
                Go to Home
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutCancel;
